import { supabase } from './supabase'

const T = 'tenblock_scores'

export type Row = { nickname: string; score: number; stage: number }

/** 게임 시작 시 호출 → 서버 서명 토큰 발급(점수 제출 검증용). 실패 시 null. */
export async function startGame(): Promise<string | null> {
  const { data, error } = await supabase.rpc('start_game')
  if (error) return null
  return (data as string) ?? null
}

/**
 * 점수 제출. 테이블 직접 INSERT가 아니라 서버측 검증 RPC(submit_score)를 통해 저장.
 * (상한·경과시간·레이트리밋 검증 후 서버가 INSERT — 클라이언트 직접 삽입은 차단됨)
 */
export async function submitScore(input: {
  nickname: string
  score: number
  stage: number
  token: string
  countryCode?: string
}): Promise<void> {
  const { error } = await supabase.rpc('submit_score', {
    p_nickname: input.nickname,
    p_score: input.score,
    p_stage: input.stage,
    p_token: input.token,
    p_country: input.countryCode ?? null,
  })
  if (error) throw error
}

export async function topScores(
  limit = 20,
  since?: string,
  dedupeByNickname = false,
): Promise<Array<{ nickname: string; score: number; stage: number }>> {
  let q = supabase.from(T).select('nickname,score,stage')
  if (since) q = q.gte('created_at', since)
  // 중복 제거 시 충분한 후보를 받아 닉네임별 최고점만 남긴다(점수 desc → 첫 등장이 최고점).
  const fetchLimit = dedupeByNickname ? Math.max(limit * 8, 200) : limit
  const { data, error } = await q.order('score', { ascending: false }).limit(fetchLimit)
  if (error) throw error
  const rows = data ?? []
  if (!dedupeByNickname) return rows
  const seen = new Set<string>()
  const unique: Array<{ nickname: string; score: number; stage: number }> = []
  for (const r of rows) {
    if (seen.has(r.nickname)) continue
    seen.add(r.nickname)
    unique.push(r)
    if (unique.length >= limit) break
  }
  return unique
}

/** 기간 필터용 ISO 시각. 'daily'(오늘 0시~) | 'weekly'(최근 7일) */
export function sinceFor(period: 'daily' | 'weekly'): string {
  if (period === 'weekly') return new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}

export async function rankOf(score: number): Promise<number> {
  const { count, error } = await supabase
    .from(T)
    .select('*', { count: 'exact', head: true })
    .gt('score', score)
  if (error) throw error
  return (count ?? 0) + 1
}

/** 오늘(일간) 기준, 주어진 점수보다 높은 기록 수 + 1 = 현재 점수의 일간 예상 등수 */
export async function rankOfDaily(score: number): Promise<number> {
  const { count, error } = await supabase
    .from(T)
    .select('*', { count: 'exact', head: true })
    .gte('created_at', sinceFor('daily'))
    .gt('score', score)
  if (error) throw error
  return (count ?? 0) + 1
}

/**
 * 일간 랭킹에서 내 점수 주변(위 span명 + 아래 span명) + 내 예상 등수.
 * above: 나보다 높은 점수 중 가까운 순(위→아래 표시 순서로 정렬됨)
 * below: 나보다 낮은 점수 중 가까운 순(위→아래)
 */
export async function dailyAround(
  score: number,
  span = 2,
): Promise<{ rank: number; above: Row[]; below: Row[] }> {
  const since = sinceFor('daily')
  const [rank, aboveRes, belowRes] = await Promise.all([
    rankOfDaily(score),
    supabase.from(T).select('nickname,score,stage').gte('created_at', since).gt('score', score)
      .order('score', { ascending: true }).limit(span),
    supabase.from(T).select('nickname,score,stage').gte('created_at', since).lt('score', score)
      .order('score', { ascending: false }).limit(span),
  ])
  if (aboveRes.error) throw aboveRes.error
  if (belowRes.error) throw belowRes.error
  // above는 오름차순(가까운 위가 먼저) → 표시상 위쪽이 더 높은 등수가 되도록 뒤집기
  return { rank, above: (aboveRes.data ?? []).reverse(), below: belowRes.data ?? [] }
}
